import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** FV_avg = ΣFV_i / n — hood face with 3×4 grid traverse and per-point readings. */
export function DiagramFumehoodMultiPointAverage() {
  const fx = 85, fy = 70, fw = 180, fh = 140;
  const rows = 3, cols = 4;
  const pad = 18;
  const x0 = fx + pad, y0 = fy + pad;
  const ddx = (fw - pad * 2) / (cols - 1);
  const ddy = (fh - pad * 2) / (rows - 1);
  // Sample readings (fpm) varying around ~100
  const readings = [95, 102, 108, 98, 105, 115, 110, 100, 92, 99, 104, 96];

  return (
    <DiagramCanvas>
      {/* Face outline (dashed capture plane) */}
      <Rect x={fx} y={fy} width={fw} height={fh} fill="rgba(255,107,26,0.05)" stroke={DT.varColor} strokeWidth="1.6" strokeDasharray="6,3" />
      <SvgText x={fx + fw / 2} y={fy - 6} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">sash opening (face plane)</SvgText>

      {/* Equal-area cell partitions */}
      {[1, 2, 3].map(i => (
        <Line key={`v${i}`} x1={fx + (fw / cols) * i} y1={fy} x2={fx + (fw / cols) * i} y2={fy + fh}
          stroke={DT.dim} strokeWidth="0.5" strokeDasharray="3,3" />
      ))}
      {[1, 2].map(i => (
        <Line key={`h${i}`} x1={fx} y1={fy + (fh / rows) * i} x2={fx + fw} y2={fy + (fh / rows) * i}
          stroke={DT.dim} strokeWidth="0.5" strokeDasharray="3,3" />
      ))}

      {/* Measurement points with readings */}
      {Array.from({ length: rows * cols }).map((_, idx) => {
        const r = Math.floor(idx / cols), c = idx % cols;
        const px = x0 + c * ddx, py = y0 + r * ddy;
        return (
          <G key={idx}>
            <Circle cx={px} cy={py} r={10} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.2" />
            <Circle cx={px} cy={py} r={1.8} fill={DT.varColor} />
            <SvgText x={px} y={py - 13} fill={DT.varColor} fontSize="6" fontFamily={DT.mono} textAnchor="middle">{readings[idx]}</SvgText>
          </G>
        );
      })}

      {/* Anemometer probe with cable leading to meter */}
      <Line x1={x0 + ddx * 2} y1={y0 + ddy} x2={305} y2={45} stroke={DT.stroke} strokeWidth="1.2" />
      <Rect x={285} y={25} width={44} height={30} rx="3" fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <Rect x={289} y={30} width={36} height={14} fill="#001020" stroke={DT.varColor} strokeWidth="0.5" />
      <SvgText x={307} y={40} fill="#6CFFB3" fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">102</SvgText>
      <SvgText x={307} y={52} fill={DT.desc} fontSize="6" fontFamily={DT.mono} textAnchor="middle">fpm</SvgText>

      {/* Summation strip */}
      <Rect x={20} y={220} width={310} height={22} fill="rgba(0,0,0,0.55)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={30} y={235} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700">Σ FV_i</SvgText>
      <SvgText x={90} y={235} fill={DT.desc} fontSize="9" fontFamily={DT.mono}>n = {rows * cols}</SvgText>
      <SvgText x={160} y={235} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700">→ FV_avg</SvgText>

      {/* Labels */}
      <LeaderLine x1={x0} y1={y0} x2={22} y2={42} varName="FV_i" desc="reading at pt i" anchor="start" />
      <LeaderLine x1={307} y1={55} x2={335} y2={130} varName="anemometer" desc="hot-wire probe" anchor="end" />

      <FormulaTag formula="FV_avg = Σ FV_i / n   (ASHRAE 110, grid traverse)" />
    </DiagramCanvas>
  );
}
